/**
 * Utilitaires de Performance - Anamnesis Interface
 * Optimisations pour composants lourds et interactions utilisateur
 */

import { computed, ref, type ComputedRef, type Ref } from 'vue';

// =================== DEBOUNCING ===================

/**
 * Fonction de debounce générique avec TypeScript strict
 */
export function debounce<T extends (...args: any[]) => void>(
  func: T,
  wait: number,
  options: { leading?: boolean; trailing?: boolean } = { trailing: true }
): (...args: Parameters<T>) => void {
  let timeout: ReturnType<typeof setTimeout> | null = null;
  let lastArgs: Parameters<T>;

  const debounced = (...args: Parameters<T>): void => {
    lastArgs = args;

    const later = (): void => {
      timeout = null;
      if (options.trailing) {
        func(...lastArgs);
      }
    };

    const callNow = options.leading && !timeout;
    
    if (timeout) {
      clearTimeout(timeout);
    }
    
    timeout = setTimeout(later, wait);
    
    if (callNow) {
      func(...args);
    }
  };

  return debounced;
}

/**
 * Debounced ref - Crée une ref debouncée pour les inputs
 */
export function useDebouncedRef<T>(
  initialValue: T,
  delay: number = 300
): {
  debouncedValue: Ref<T>;
  immediateValue: Ref<T>;
  updateValue: (value: T) => void;
} {
  const immediateValue = ref(initialValue) as Ref<T>;
  const debouncedValue = ref(initialValue) as Ref<T>;

  const updateDebounced = debounce((value: any) => {
    debouncedValue.value = value;
  }, delay);

  const updateValue = (value: T): void => {
    immediateValue.value = value;
    updateDebounced(value);
  };

  return {
    debouncedValue,
    immediateValue,
    updateValue
  };
}

// =================== MEMOIZATION ===================

/**
 * Cache simple pour memoization avec TTL
 */
interface MemoCache<T> {
  value: T;
  timestamp: number;
  dependencies: string;
}

const memoCache = new Map<string, MemoCache<unknown>>();

/**
 * Memoization avec gestion des dépendances et TTL
 */
export function memoize<T>(
  fn: () => T,
  dependencies: unknown[],
  ttl: number = 5000, // 5 secondes par défaut
  key?: string
): T {
  const cacheKey = key || fn.toString();
  const depsString = JSON.stringify(dependencies);
  const cached = memoCache.get(cacheKey) as MemoCache<T> | undefined;

  // Vérifier si le cache est valide
  if (cached && 
      cached.dependencies === depsString && 
      Date.now() - cached.timestamp < ttl) {
    return cached.value;
  }

  // Calculer la nouvelle valeur
  const newValue = fn();
  
  // Mettre en cache
  memoCache.set(cacheKey, {
    value: newValue,
    timestamp: Date.now(),
    dependencies: depsString
  });

  return newValue;
}

/**
 * Computed memoizé avec contrôle des dépendances
 */
export function useMemoizedComputed<T>(
  fn: () => T,
  dependencies: Ref<unknown>[],
  ttl: number = 5000
): ComputedRef<T> {
  return computed(() => {
    const deps = dependencies.map(dep => dep.value);
    return memoize(fn, deps, ttl);
  });
}

// =================== OPTIMISATION LISTES ===================

/**
 * Filtre optimisé pour grandes listes avec memoization
 */
export function useOptimizedFilter<T>(
  items: Ref<T[]>,
  filterFn: (item: T) => boolean,
  dependencies: Ref<unknown>[] = [],
  ttl: number = 2000
): ComputedRef<T[]> {
  return useMemoizedComputed(
    () => items.value.filter(filterFn),
    [items, ...dependencies],
    ttl
  );
}

/**
 * Recherche optimisée dans les objets
 */
export function createOptimizedSearch<T extends Record<string, unknown>>(
  searchFields: (keyof T)[],
  caseSensitive: boolean = false
): (items: T[], query: string) => T[] {
  return memoize(
    () => (items: T[], query: string): T[] => {
      if (!query.trim()) return items;
      
      const searchTerm = caseSensitive ? query : query.toLowerCase();
      
      return items.filter(item => {
        return searchFields.some(field => {
          const value = item[field];
          if (typeof value === 'string') {
            const searchValue = caseSensitive ? value : value.toLowerCase();
            return searchValue.includes(searchTerm);
          }
          return false;
        });
      });
    },
    [searchFields, caseSensitive],
    10000 // Cache plus long pour les fonctions de recherche
  );
}

// =================== OPTIMISATION DOM ===================

/**
 * Lazy loading pour composants lourds
 */
export function useLazyComponent<T>(
  componentLoader: () => Promise<T>,
  delay: number = 100
): {
  component: Ref<T | null>;
  isLoading: Ref<boolean>;
  loadComponent: () => void;
} {
  const component = ref<T | null>(null);
  const isLoading = ref(false);

  const loadComponent = debounce(async () => {
    if (component.value || isLoading.value) return;
    
    isLoading.value = true;
    try {
      component.value = await componentLoader();
    } catch (error) {
      console.error('Erreur lors du chargement du composant:', error);
    } finally {
      isLoading.value = false;
    }
  }, delay);

  return {
    component: component as Ref<T | null>,
    isLoading,
    loadComponent
  };
}

/**
 * Intersection Observer pour lazy loading
 */
export function useIntersectionObserver(
  callback: () => void,
  options: IntersectionObserverInit = { threshold: 0.1 }
): {
  targetRef: Ref<Element | null>;
  isIntersecting: Ref<boolean>;
} {
  const targetRef = ref<Element | null>(null);
  const isIntersecting = ref(false);

  let observer: IntersectionObserver | null = null;

  const createObserver = (): void => {
    if (observer) {
      observer.disconnect();
    }

    observer = new IntersectionObserver(([entry]) => {
      isIntersecting.value = entry.isIntersecting;
      if (entry.isIntersecting) {
        callback();
      }
    }, options);

    if (targetRef.value) {
      observer.observe(targetRef.value);
    }
  };

  // Watch pour créer l'observer quand l'élément est disponible
  const stopWatching = computed(() => {
    if (targetRef.value && !observer) {
      createObserver();
    }
    return targetRef.value;
  });

  // Utiliser le watcher pour éviter l'erreur unused
  void stopWatching.value;

  return {
    targetRef,
    isIntersecting
  };
}

// =================== UTILITAIRES PERFORMANCE ===================

/**
 * Mesure de performance simple
 */
export function measurePerformance<T>(
  name: string,
  fn: () => T,
  logResult: boolean = true
): T {
  const start = performance.now();
  const result = fn();
  const end = performance.now();
  
  if (logResult) {
    console.log(`⚡ ${name}: ${(end - start).toFixed(2)}ms`);
  }
  
  return result;
}

/**
 * Batch des updates pour éviter les re-renders multiples
 */
export function useBatchedUpdates<T>(): {
  batchedValue: Ref<T[]>;
  addToBatch: (value: T) => void;
  processBatch: () => void;
  clearBatch: () => void;
} {
  const batchedValue = ref<T[]>([]) as Ref<T[]>;
  const pendingBatch = ref<T[]>([]) as Ref<T[]>;

  const processBatch = debounce(() => {
    batchedValue.value = [...pendingBatch.value];
    pendingBatch.value = [];
  }, 16); // ~60fps

  const addToBatch = (value: T): void => {
    pendingBatch.value.push(value);
    processBatch();
  };

  const clearBatch = (): void => {
    pendingBatch.value = [];
    batchedValue.value = [];
  };

  return {
    batchedValue,
    addToBatch,
    processBatch,
    clearBatch
  };
}

/**
 * Nettoyage du cache memoization
 */
export function clearMemoCache(pattern?: string): void {
  if (pattern) {
    const regex = new RegExp(pattern);
    for (const [key] of memoCache) {
      if (regex.test(key)) {
        memoCache.delete(key);
      }
    }
  } else {
    memoCache.clear();
  }
}

/**
 * Statistiques du cache pour debug
 */
export function getMemoStats(): {
  size: number;
  keys: string[];
  oldestEntry: number;
  newestEntry: number;
} {
  const entries = Array.from(memoCache.entries());
  const timestamps = entries.map(([, cache]) => cache.timestamp);
  
  return {
    size: memoCache.size,
    keys: Array.from(memoCache.keys()),
    oldestEntry: timestamps.length > 0 ? Math.min(...timestamps) : 0,
    newestEntry: timestamps.length > 0 ? Math.max(...timestamps) : 0
  };
} 